<i18n>
{
  "en": {
    "loadDataError": "There was an error loading the data.",
    "savingError": "There was an error saving the prices",
    "exportPricesChangesActiveWaitMessage": "Generating product price change list.",
    "exportPriceChangeError": "Error loading price change list. Please, try again later."
  },
  "es": {
    "loadDataError": "Ha habido un error cargando los datos.",
    "savingError": "Ha habido un error guardando los precios",
    "exportPricesChangesActiveWaitMessage": "Generando el archivo de cambio de precios de productos.",
    "exportPriceChangeError": "Ha ocurrido un error generando el archivo de cambio de precios."
  }
}
</i18n>

<template>
  <div class="d-flex flex-column height-100 pb-8">
    <price-manager-filters ref="priceFilters" :hasChanges="hasChanges"
                           @apply-filters="onApplyFilters"
                           @save="onSave" @export-price-changes="onExportPricesChange"
                           class="mt-1"/>
    <price-table v-if="!isLoading" @price-changed="onPriceChanged"
                 :frontPrices="frontPricesToFilter"
                 @undo="onUndo"
                 class="flex-grow-1 mt-2"/>
    <dynameat-skeleton v-else skeletonType="table"/>
    <product-kpi-panel v-if="productGuid" :productGuid="productGuid" :product-name="productName"
                       @close="onCloseKpiPanel"/>
    <dynameat-confirmation-modal :visible="exportPricesChangesLoading"
                                 :title="$t('exportPricesChangesActiveWaitMessage')"
                                 :is-loading="exportPricesChangesLoading" description=""
                                 confirm-text=""/>
  </div>
</template>

<script>

import Vue from 'vue';
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import PriceManagerFilters
  from './components/PriceManagerFilters.vue';
import ProductKpiPanel from '../../components/ProductKpiPanel.vue';
import { EventBus } from '../../helpers/eventBus';
import { FrontPriceList } from './models';

@Component
@Components({
  PriceTable: () => import('./components/PriceTable.vue'),
  DynameatSkeleton,
  PriceManagerFilters,
  ProductKpiPanel,
  DynameatConfirmationModal
})
@MapActions(['getFrontPriceListRequest', 'updateFrontPriceListRequest', 'setLoading',
  'applyFrontPriceListChanges', 'addNotification', 'restrictionIntervalListRequest',
  'exportPriceChangesRequest'])
@MapGetters(['frontPriceList', 'isLoading', 'menuFilterList', 'selectedPointOfSale'])
export default class PriceManager extends Vue {
  priceChanges = [];

  saleRateGuid = '';

  startDate = '';

  endDate = '';

  menuGuidToFilter = '';

  sectionGuidToFilter = '';

  productGuid = null;

  productName = '';

  exportPricesChangesLoading = false;

  get hasChanges() {
    return this.priceChanges.length !== 0;
  }

  get showTable() {
    return this.saleRateGuid && this.endDate && this.startDate;
  }

  get frontPricesToFilter() {
    if (this.showTable) {
      const productGuids = (this.menuGuidToFilter) ? this.productGuidsToFilter : [];
      const prices = (productGuids.length) ? this.frontPriceList.priceList.filter((price) => productGuids.includes(price.productGuid)) : [];

      return (this.menuGuidToFilter) ? new FrontPriceList(this.frontPriceList.saleRateGuid, this.frontPriceList.startDate, this.frontPriceList.endDate, this.frontPriceList.groupList, prices) : this.frontPriceList;
    }
    return null;
  }

  get productGuidsToFilter() {
    let sectionToFilter = null;
    const menuToFilter = this.menuFilterList.find((menuFilter) => menuFilter.menuGuid === this.menuGuidToFilter);

    if (this.sectionGuidToFilter) {
      sectionToFilter = menuToFilter.sections.find((sectionFilter) => sectionFilter.sectionGuid === this.sectionGuidToFilter);
    }
    return (sectionToFilter) ? sectionToFilter.allProductGuids : menuToFilter.sections.flatMap((section) => section.allProductGuids);
  }

  async created() {
    EventBus.$on('product', this.onProduct);
    try {
      await this.restrictionIntervalListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('loadDataError')));
    }
  }

  mounted() {
    this.priceChanges = [];
    this.saleRateGuid = '';
    this.startDate = '';
    this.endDate = '';
    this.menuGuidToFilter = '';
    this.sectionGuidToFilter = '';
    this.productGuid = null;
    this.productName = '';
    this.exportPricesChangesLoading = false;
  }

  destroyed() {
    EventBus.$off('product', this.onProduct);
  }

  onProduct(productGuid, productName) {
    this.productGuid = productGuid;
    this.productName = productName;
  }

  async loadData() {
    try {
      if (this.saleRateGuid && this.startDate && this.endDate) {
        await this.setLoading(1);
        await this.getFrontPriceListRequest({
          saleRateGuid: this.saleRateGuid,
          startDate: this.startDate,
          endDate: this.endDate
        });
      }
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('loadDataError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  onPriceChanged(changes) {
    this.priceChanges.push(...changes);
  }

  onCloseKpiPanel() {
    this.productGuid = null;
  }

  async onSave() {
    try {
      await this.setLoading(1);
      await this.applyFrontPriceListChanges(this.priceChanges);
      await this.updateFrontPriceListRequest({ pricesToUpdate: this.frontPriceList.getChangedList() });
      this.priceChanges = [];
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('savingError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  async onApplyFilters(payload) {
    const {
      dates, menu, section, saleRate
    } = payload;
    this.saleRateGuid = saleRate;
    this.startDate = dates.startDate;
    this.endDate = dates.endDate;
    this.menuGuidToFilter = menu;
    this.sectionGuidToFilter = section;

    this.discardChanges();
    if (this.needToUpdatePrices()) await this.loadData();
  }

  async onExportPricesChange({ startDate, endDate }) {
    try {
      this.exportPricesChangesLoading = true;

      const payload = {
        posGuid: this.selectedPointOfSale.guid,
        startDate,
        endDate
      };
      await this.exportPriceChangesRequest(payload);
    } catch {
      this.addNotification(createErrorNotification(this.$t('exportPriceChangeError'), null, 3000));
    } finally {
      this.exportPricesChangesLoading = false;
    }
  }

  discardChanges() {
    this.priceChanges = [];
  }

  needToUpdatePrices() {
    return !this.frontPriceList || this.frontPriceList.saleRateGuid !== this.saleRateGuid
      || this.frontPriceList.startDate !== this.startDate
      || this.frontPriceList.endDate !== this.endDate;
  }

  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      const answer = this.$refs.priceFilters.checkRouteLeave();
      next(answer);
    } else next();
  }

  onUndo(numberOfChanges = 1) {
    for (let i = 0; i < numberOfChanges; i++) {
      this.priceChanges.pop();
    }
  }
}

</script>

<style scoped>
.height-100 {
  height: 100%;
}
</style>
